import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator,
  BarChart3,
  Scan,
  Thermometer,
  FileText,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  ArrowUpRight,
  Activity,
  ShieldCheck,
  Search,
  Download,
  Sliders,
  ChevronDown,
  ChevronUp,
  XCircle,
  TrendingDown,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useAuthStore } from '../stores/auth';
import { toast } from 'sonner';

// Finding Data Interface
interface Finding {
  id: string;
  assetTag: string;
  component: string;
  system: string;
  opTemp: number;
  method: 'Radiometric IR' | 'Visual AI' | 'PEC Screening' | 'Ultrasonic UT';
  nominalWall: number;
  measuredWall: number;
  rsf: number;
  ipsScore: number;
  status: 'PASS_MAWP' | 'RERATE_REQUIRED' | 'REPAIR_MANDATORY';
  workOrder: string;
  workOrderStatus: 'synced' | 'pending' | 'not_required';
  lastInspected: string;
  coordinates: string;
  details: {
    foliasMt: number;
    flawLength: number;
    mawp: number;
    deratedMawp?: number;
    corrosionAllowance: number;
    recommendedAction: string;
  };
}

// Initial Mock Findings adhering to authentic API 579 & API RP 583 engineering values
const INITIAL_FINDINGS: Finding[] = [
  {
    id: 'FND-0412',
    assetTag: '12"-HC-2019-CS',
    component: 'Long-Radius 90° Elbow',
    system: 'Hydrocracker Reactor Feed',
    opTemp: 84, // In API RP 583 danger window (-4°C to 120°C)
    method: 'PEC Screening',
    nominalWall: 12.7,
    measuredWall: 6.85,
    rsf: 0.884,
    ipsScore: 8.7,
    status: 'RERATE_REQUIRED',
    workOrder: 'SAP-WO-884912',
    workOrderStatus: 'synced',
    lastInspected: '2 hours ago',
    coordinates: 'Grid B-4 / Elev +14.2m',
    details: {
      foliasMt: 1.412,
      flawLength: 160,
      mawp: 3.45,
      deratedMawp: 2.92,
      corrosionAllowance: 1.2,
      recommendedAction: 'Immediate engineering rerate to 2.92 MPa; schedule localized clamp prior to Q4 turnaround.',
    },
  },
  {
    id: 'FND-0208',
    assetTag: 'TK-402A-NOZ-N1',
    component: 'Vessel Inlet Nozzle 8"',
    system: 'Sour Water Stripper',
    opTemp: 62,
    method: 'Radiometric IR',
    nominalWall: 10.3,
    measuredWall: 9.6,
    rsf: 0.965,
    ipsScore: 6.4,
    status: 'PASS_MAWP',
    workOrder: 'N/A',
    workOrderStatus: 'not_required',
    lastInspected: '5 hours ago',
    coordinates: 'Tank Farm 4 / Shell Course 3',
    details: {
      foliasMt: 1.12,
      flawLength: 45,
      mawp: 2.1,
      corrosionAllowance: 2.4,
      recommendedAction: 'Insulation is dry, weather-seal is sound. Re-inspect on standard 36-month RBI cycle.',
    },
  },
  {
    id: 'FND-0891',
    assetTag: '06"-CDU-1044-A106B',
    component: 'Piping Low Point & Drain',
    system: 'Atmospheric Column Bottoms',
    opTemp: 98,
    method: 'Visual AI',
    nominalWall: 8.56,
    measuredWall: 4.12,
    rsf: 0.742,
    ipsScore: 9.4,
    status: 'REPAIR_MANDATORY',
    workOrder: 'MAXIMO-78210',
    workOrderStatus: 'synced',
    lastInspected: 'Yesterday',
    coordinates: 'Pipe Rack A2 / Ground Sump',
    details: {
      foliasMt: 1.78,
      flawLength: 210,
      mawp: 1.85,
      deratedMawp: 1.24,
      corrosionAllowance: 0.4,
      recommendedAction: 'Severe pitting under support shoe. Scaffolding dispatched for full spool replacement.',
    },
  },
  {
    id: 'FND-0544',
    assetTag: 'V-102-TOP-HEAD',
    component: '2:1 Ellipsoidal Head',
    system: 'Amine Regeneration Tower',
    opTemp: 110,
    method: 'Radiometric IR',
    nominalWall: 16.0,
    measuredWall: 14.8,
    rsf: 0.952,
    ipsScore: 5.8,
    status: 'PASS_MAWP',
    workOrder: 'N/A',
    workOrderStatus: 'not_required',
    lastInspected: '2 days ago',
    coordinates: 'Column V-102 / Platform 5',
    details: {
      foliasMt: 1.09,
      flawLength: 80,
      mawp: 2.75,
      corrosionAllowance: 3.1,
      recommendedAction: 'Superficial cladding stain without thermal anomaly. Wall loss within acceptable FCA limits.',
    },
  },
  {
    id: 'FND-0619',
    assetTag: '10"-VDU-3302-CS',
    component: 'Straight Run (Flange Vicinity)',
    system: 'Vacuum Distillation Overhead',
    opTemp: 45,
    method: 'Ultrasonic UT',
    nominalWall: 9.27,
    measuredWall: 5.1,
    rsf: 0.812,
    ipsScore: 8.1,
    status: 'RERATE_REQUIRED',
    workOrder: 'SAP-WO-885004',
    workOrderStatus: 'pending',
    lastInspected: '3 days ago',
    coordinates: 'Unit 3 / Overhead Rack',
    details: {
      foliasMt: 1.54,
      flawLength: 175,
      mawp: 1.6,
      deratedMawp: 1.18,
      corrosionAllowance: 0.8,
      recommendedAction: 'Local metal loss exceeds allowable t_min for full design pressure. Rerate workflow initiated.',
    },
  },
  {
    id: 'FND-0731',
    assetTag: 'EX-201-SHELL-B',
    component: 'Heat Exchanger Shell',
    system: 'Naphtha Hydrotreater',
    opTemp: 78,
    method: 'PEC Screening',
    nominalWall: 14.2,
    measuredWall: 13.9,
    rsf: 0.988,
    ipsScore: 4.2,
    status: 'PASS_MAWP',
    workOrder: 'N/A',
    workOrderStatus: 'not_required',
    lastInspected: '4 days ago',
    coordinates: 'Train 2 Exchanger Bank',
    details: {
      foliasMt: 1.04,
      flawLength: 30,
      mawp: 3.1,
      corrosionAllowance: 4.2,
      recommendedAction: 'No active degradation. Insulation blanket integrity certified.',
    },
  },
];

// Temperature Spectrum Chart Data showing API RP 583 CUI Danger Window (-4°C to 120°C)
const TEMP_DISTRIBUTION_DATA = [
  { tempRange: '< -4°C (Cryo)', assets: 142, cuiVulnerability: 'Negligible (Ice barrier)', riskLevel: 'low' },
  { tempRange: '-4°C - 30°C', assets: 284, cuiVulnerability: 'Moderate (Intermittent condensation)', riskLevel: 'medium' },
  { tempRange: '30°C - 60°C', assets: 412, cuiVulnerability: 'High (Optimal corrosion rate)', riskLevel: 'high' },
  { tempRange: '60°C - 95°C', assets: 498, cuiVulnerability: 'Extreme (Peak API RP 583 zone)', riskLevel: 'critical' },
  { tempRange: '95°C - 120°C', assets: 320, cuiVulnerability: 'High (Boiling/evaporative cycles)', riskLevel: 'high' },
  { tempRange: '> 120°C', assets: 215, cuiVulnerability: 'Low (Insulation stays dry)', riskLevel: 'low' },
];

// Defect classification breakdown
const DEFECT_BREAKDOWN_DATA = [
  { name: 'Jacketing Tears & Punctures', count: 38, color: '#3b82f6' },
  { name: 'Wet Calcium Silicate / Mineral Wool', count: 24, color: '#f59e0b' },
  { name: 'Severe Local Metal Loss', count: 12, color: '#ef4444' },
  { name: 'Sealant / Caulk Degradation', count: 45, color: '#8b5cf6' },
  { name: 'Pipe Shoe / Saddle Moisture Traps', count: 19, color: '#10b981' },
];

// Monthly screening vs saved scaffolding
const MONTHLY_TREND_DATA = [
  { month: 'Apr', screened: 120, blanketPrevented: 88, scaffoldingSaved: 78 },
  { month: 'May', screened: 165, blanketPrevented: 124, scaffoldingSaved: 110 },
  { month: 'Jun', screened: 190, blanketPrevented: 152, scaffoldingSaved: 135 },
  { month: 'Jul', screened: 240, blanketPrevented: 185, scaffoldingSaved: 164 },
  { month: 'Aug', screened: 285, blanketPrevented: 228, scaffoldingSaved: 202 },
  { month: 'Sep', screened: 310, blanketPrevented: 245, scaffoldingSaved: 218 },
];

const TOOLS_NAVIGATION = [
  {
    name: 'FFS Calculator',
    description: 'API 579-1 Part 5 Level 1 Assessment',
    icon: Calculator,
    href: '/tools/ffs',
    color: 'bg-blue-600',
  },
  {
    name: 'Priority Score (IPS)',
    description: 'Risk-based inspection prioritization',
    icon: BarChart3,
    href: '/tools/ips',
    color: 'bg-purple-600',
  },
  {
    name: 'Cladding Defect AI',
    description: 'YOLOv8 Jacketing defect segmentation',
    icon: Scan,
    href: '/tools/cladding',
    color: 'bg-emerald-600',
  },
  {
    name: 'Thermal Analysis',
    description: 'Radiometric moisture mapping',
    icon: Thermometer,
    href: '/tools/thermal',
    color: 'bg-amber-600',
  },
  {
    name: 'PDF Parser',
    description: 'Legacy report digitization',
    icon: FileText,
    href: '/tools/pdf-parser',
    color: 'bg-rose-600',
  },
  {
    name: 'CMMS Dispatch',
    description: 'SAP PM & IBM Maximo work orders',
    icon: Wrench,
    href: '/cmms',
    color: 'bg-indigo-600',
  },
];

export function DashboardPage() {
  const { user } = useAuthStore();
  
  // Filtering & Search states
  const [selectedFacility, setSelectedFacility] = useState('Rotterdam Complex - CDU / VDU Train 1');
  const [activeStatusTab, setActiveStatusTab] = useState<'ALL' | 'CRITICAL' | 'RERATE' | 'PASS'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFindingId, setExpandedFindingId] = useState<string | null>('FND-0412');
  
  // Mini FFS Calculator Drawer State
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  const [calcInput, setCalcInput] = useState({
    nominalThickness: 12.7,
    measuredThickness: 7.2,
    flawLength: 150,
    diameter: 219.1,
  });
  const [calcResult, setCalcResult] = useState<{ rsf: number; pass: boolean } | null>(null);

  // Run quick FFS calculation
  const handleQuickCalculate = () => {
    const { nominalThickness, measuredThickness, flawLength, diameter } = calcInput;
    if (nominalThickness <= 0 || measuredThickness <= 0) {
      toast.error('Please enter valid thickness values');
      return;
    }
    const rt = measuredThickness / nominalThickness;
    const lambda = (1.285 * flawLength) / Math.sqrt(diameter * nominalThickness);
    const mt = Math.sqrt(1 + 0.48 * Math.pow(lambda, 2));
    const calculatedRsf = rt / (1 - (1 / mt) * (1 - rt));
    const roundedRsf = Math.round(calculatedRsf * 1000) / 1000;
    const pass = roundedRsf >= 0.90;

    setCalcResult({ rsf: roundedRsf, pass });
    toast.success(`Quick API 579 RSF: ${roundedRsf} (${pass ? 'PASS' : 'RERATE'})`);
  };

  // Filtered findings list
  const filteredFindings = useMemo(() => {
    return INITIAL_FINDINGS.filter((f) => {
      // Tab filter
      if (activeStatusTab === 'CRITICAL' && f.status !== 'REPAIR_MANDATORY') return false;
      if (activeStatusTab === 'RERATE' && f.status !== 'RERATE_REQUIRED') return false;
      if (activeStatusTab === 'PASS' && f.status !== 'PASS_MAWP') return false;

      // Search filter
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        return (
          f.assetTag.toLowerCase().includes(query) ||
          f.component.toLowerCase().includes(query) ||
          f.system.toLowerCase().includes(query) ||
          f.id.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [activeStatusTab, searchQuery]);

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Top Industrial Operational Command Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="font-mono text-xs font-semibold text-slate-500 uppercase tracking-wider">
                INTEGRITY AUDIT LOG // KERNEL v2.4
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Asset Integrity Intelligence Console
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Deterministic CUI screening, remaining wall thickness, and API 579-1 Part 5 assessments.
            </p>
          </div>

          {/* Plant Unit Selector & Quick Tools */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <select
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                className="bg-slate-50 border border-slate-300 text-slate-800 text-xs sm:text-sm font-semibold rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                <option>Rotterdam Complex - CDU / VDU Train 1</option>
                <option>North Sea Bravo - Gas Processing Platform</option>
                <option>Jubail Petrochemical - Olefins Cracker #3</option>
                <option>Texas City - Alkylation & Hydrotreater</option>
              </select>
            </div>

            <button
              onClick={() => setIsCalcOpen(!isCalcOpen)}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-3 flex items-center gap-1.5 shadow-sm"
            >
              <Sliders className="h-4 w-4 text-blue-600" />
              <span>Quick FFS Scratchpad</span>
            </button>

            <button
              onClick={() => toast.success('Exporting certified plant integrity report (PDF)...')}
              className="btn btn-primary text-xs sm:text-sm py-2 px-3 flex items-center gap-1.5 shadow-sm"
            >
              <Download className="h-4 w-4" />
              <span>Export Dossier</span>
            </button>
          </div>

        </div>

        {/* Operational Facility Banner Preview */}
        <div className="mt-4 relative rounded-xl overflow-hidden border border-slate-200 shadow-sm h-36 sm:h-44 group">
          <img
            src="/banner.png"
            alt="Field CUI Inspection"
            className="w-full h-full object-cover object-center group-hover:scale-102 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-transparent flex flex-col justify-center p-5 text-white">
            <div className="flex items-center gap-2 mb-1">
              <span className="telemetry-pill bg-blue-600 text-white font-bold text-[10px]">
                ON-STREAM TELEMETRY ACTIVE
              </span>
              <span className="font-mono text-xs text-slate-300">
                DRONE IR & PEC CONTINUOUS CAMPAIGN
              </span>
            </div>
            <h3 className="text-lg sm:text-xl font-bold tracking-tight">
              {selectedFacility}
            </h3>
            <p className="text-xs text-slate-300 max-w-lg mt-1 hidden sm:block">
              Continuous radiometric thermal monitoring, automated cladding defect segmentation, and deterministic API 579-1 calculations.
            </p>
          </div>
        </div>

        {/* Real-time Telemetry Status Bar */}
        <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 text-xs font-mono text-slate-600">
          <div className="flex flex-wrap items-center gap-4">
            <span>PLANT INTEGRITY: <strong className="text-green-600">96.4% NORMAL</strong></span>
            <span>LAST SCAN SYNC: <strong>14 MINS AGO</strong></span>
            <span>CALIBRATION: <strong className="text-blue-600">NIST CERTIFIED</strong></span>
          </div>
          <div className="flex items-center gap-2 text-slate-500">
            <span>OPERATOR: <strong>{user?.name || 'Lead Integrity Engineer'}</strong></span>
            <span>({user?.organization || 'Sensifai Enterprise'})</span>
          </div>
        </div>
      </div>

      {/* Quick FFS Assessment Mini-Drawer (Expandable) */}
      {isCalcOpen && (
        <div className="bg-slate-900 text-white rounded-xl p-5 border border-slate-800 shadow-xl space-y-4 animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-400" />
              <h3 className="text-sm font-bold tracking-wide">
                Quick In-Line API 579-1 Part 5 Level 1 RSF Scratchpad
              </h3>
            </div>
            <button
              onClick={() => setIsCalcOpen(false)}
              className="text-slate-400 hover:text-white text-xs font-mono"
            >
              ✕ Close
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="text-[11px] font-mono text-slate-400 block mb-1">
                Nominal Wall (t_nom) mm
              </label>
              <input
                type="number"
                step="0.1"
                value={calcInput.nominalThickness}
                onChange={(e) => setCalcInput({ ...calcInput, nominalThickness: Number(e.target.value) })}
                className="bg-slate-800 border border-slate-700 rounded px-2.5 py-1.5 text-sm font-mono text-white w-full focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono text-slate-400 block mb-1">
                Min Measured (t_min) mm
              </label>
              <input
                type="number"
                step="0.1"
                value={calcInput.measuredThickness}
                onChange={(e) => setCalcInput({ ...calcInput, measuredThickness: Number(e.target.value) })}
                className="bg-slate-800 border border-slate-700 rounded px-2.5 py-1.5 text-sm font-mono text-white w-full focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono text-slate-400 block mb-1">
                Axial Flaw Length (2c) mm
              </label>
              <input
                type="number"
                step="5"
                value={calcInput.flawLength}
                onChange={(e) => setCalcInput({ ...calcInput, flawLength: Number(e.target.value) })}
                className="bg-slate-800 border border-slate-700 rounded px-2.5 py-1.5 text-sm font-mono text-white w-full focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono text-slate-400 block mb-1">
                Outside Diameter (D) mm
              </label>
              <input
                type="number"
                step="1"
                value={calcInput.diameter}
                onChange={(e) => setCalcInput({ ...calcInput, diameter: Number(e.target.value) })}
                className="bg-slate-800 border border-slate-700 rounded px-2.5 py-1.5 text-sm font-mono text-white w-full focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={handleQuickCalculate}
              className="btn btn-primary text-xs py-1.5 px-4 font-mono font-bold"
            >
              Evaluate RSF Now
            </button>

            {calcResult && (
              <div className="flex items-center gap-3 font-mono text-xs">
                <span>Calculated RSF: <strong className="text-white text-sm">{calcResult.rsf}</strong></span>
                <span>(Threshold: 0.900)</span>
                <span
                  className={`px-2 py-0.5 rounded font-bold ${
                    calcResult.pass
                      ? 'bg-green-900 text-green-300 border border-green-700'
                      : 'bg-amber-900 text-amber-300 border border-amber-700'
                  }`}
                >
                  {calcResult.pass ? 'PASS - FULL MAWP' : 'RERATE REQUIRED'}
                </span>
                <Link to="/tools/ffs" className="text-blue-400 hover:underline text-xs ml-2">
                  Open full assessment →
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Industrial Key Performance Telemetry KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Card 1: Monitored Assets */}
        <div className="card p-5 border-l-4 border-l-blue-600 space-y-2">
          <div className="flex items-center justify-between text-slate-500 text-xs font-mono">
            <span>TOTAL ASSETS MONITORED</span>
            <Activity className="h-4 w-4 text-blue-600" />
          </div>
          <div className="text-3xl font-extrabold font-mono text-slate-900">
            1,871
          </div>
          <div className="flex items-center justify-between text-xs text-slate-600 pt-1 border-t border-slate-100">
            <span>1,420 Piping | 451 Vessels</span>
            <span className="text-green-600 font-semibold">+18 this month</span>
          </div>
        </div>

        {/* Card 2: Active Interventions & Critical Anomaly */}
        <div className="card p-5 border-l-4 border-l-red-500 space-y-2">
          <div className="flex items-center justify-between text-slate-500 text-xs font-mono">
            <span>CRITICAL INTERVENTIONS</span>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </div>
          <div className="text-3xl font-extrabold font-mono text-slate-900">
            4 <span className="text-xs font-sans font-normal text-slate-500">(1.2% rate)</span>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-600 pt-1 border-t border-slate-100">
            <span>6 Rerate Pending</span>
            <span className="text-red-600 font-semibold">Zero Leaks</span>
          </div>
        </div>

        {/* Card 3: Scaffolding Savings Realized */}
        <div className="card p-5 border-l-4 border-l-green-500 space-y-2">
          <div className="flex items-center justify-between text-slate-500 text-xs font-mono">
            <span>SCAFFOLDING SAVED (YTD)</span>
            <TrendingDown className="h-4 w-4 text-green-600" />
          </div>
          <div className="text-3xl font-extrabold font-mono text-slate-900">
            €824,500
          </div>
          <div className="flex items-center justify-between text-xs text-slate-600 pt-1 border-t border-slate-100">
            <span>68% vs Blanket Stripping</span>
            <span className="text-green-600 font-semibold">+€62k this cycle</span>
          </div>
        </div>

        {/* Card 4: CMMS & Work Orders In Flight */}
        <div className="card p-5 border-l-4 border-l-purple-600 space-y-2">
          <div className="flex items-center justify-between text-slate-500 text-xs font-mono">
            <span>SAP PM / MAXIMO ORDERS</span>
            <Wrench className="h-4 w-4 text-purple-600" />
          </div>
          <div className="text-3xl font-extrabold font-mono text-slate-900">
            28 <span className="text-xs font-sans font-normal text-slate-500">Active</span>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-600 pt-1 border-t border-slate-100">
            <span>22 Dispatched | 6 Pending</span>
            <span className="text-blue-600 font-semibold">100% Dossier Linked</span>
          </div>
        </div>

      </div>

      {/* Interactive Charts Suite (Recharts) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Chart 1: Temperature Window vs CUI Vulnerability (API RP 583) */}
        <div className="lg:col-span-8 card p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Thermometer className="h-4 w-4 text-amber-600" />
                  API RP 583 Temperature Susceptibility Spectrum
                </h3>
                <p className="text-xs text-slate-500">
                  Asset distribution across operating temperatures with highlighted peak CUI risk envelope (-4°C to 120°C).
                </p>
              </div>
              <span className="telemetry-pill bg-amber-50 text-amber-800 border border-amber-200">
                1,514 Assets in CUI Zone
              </span>
            </div>

            <div className="h-64 w-full mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={TEMP_DISTRIBUTION_DATA} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cuiZoneGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="tempRange" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="assets"
                    stroke="#d97706"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#cuiZoneGradient)"
                    name="Assets Count"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-mono text-slate-500">
            <span>CRITICAL BAND: 60°C - 95°C (HIGHEST CONDENSATION RATE)</span>
            <span className="text-blue-600 hover:underline cursor-pointer">Filter by temperature range →</span>
          </div>
        </div>

        {/* Chart 2: Defect Classification Breakdown */}
        <div className="lg:col-span-4 card p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Scan className="h-4 w-4 text-blue-600" />
                Defect Modality
              </h3>
              <span className="text-xs font-mono text-slate-500">138 Findings</span>
            </div>
            <p className="text-xs text-slate-500">
              Breakdown of segmented visual, thermal, and wall-loss anomalies.
            </p>

            <div className="h-56 w-full mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={DEFECT_BREAKDOWN_DATA}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={4}
                    dataKey="count"
                  >
                    {DEFECT_BREAKDOWN_DATA.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '8px', color: '#fff', fontSize: '11px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Legend Items */}
          <div className="space-y-1.5 text-xs">
            {DEFECT_BREAKDOWN_DATA.map((d, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                  <span className="text-slate-700">{d.name}</span>
                </div>
                <span className="font-mono font-bold text-slate-900">{d.count}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Chart 3: Monthly Screening Velocity & Scaffolding Reduction */}
      <div className="card p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div>
            <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-emerald-600" />
              Inspection Velocity vs. Unnecessary Scaffolding Avoided
            </h3>
            <p className="text-xs text-slate-500">
              Correlating monthly non-destructive screenings with prevented blanket strip locations and scaffolding costs.
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-blue-600" /> Screened Locations
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-green-500" /> Prevented Blind Strips
            </span>
          </div>
        </div>

        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={MONTHLY_TREND_DATA} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
              />
              <Bar dataKey="screened" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Screened Circuits" />
              <Bar dataKey="blanketPrevented" fill="#10b981" radius={[4, 4, 0, 0]} name="Stripping Avoided" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick Launchpad Analysis Tools */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">
            Integrated Analysis & Dispatch Engines
          </h2>
          <span className="text-xs font-mono text-slate-500">6 Specialized Modules</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TOOLS_NAVIGATION.map((tool, index) => (
            <Link
              key={index}
              to={tool.href}
              className="card-hover p-4 bg-white border border-slate-200 rounded-xl group flex items-start justify-between"
            >
              <div className="flex items-start gap-3.5">
                <div className={`h-11 w-11 rounded-lg ${tool.color} flex items-center justify-center flex-shrink-0 text-white shadow-sm`}>
                  <tool.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5 leading-snug">{tool.description}</p>
                </div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 transition-colors flex-shrink-0 mt-1" />
            </Link>
          ))}
        </div>
      </div>

      {/* High-Precision Active Findings & Assets Data Table */}
      <div className="card overflow-hidden bg-white border border-slate-200 rounded-xl shadow-sm">
        
        {/* Table Filter & Search Controls Header */}
        <div className="p-4 sm:p-5 border-b border-slate-200 bg-slate-50/70 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              Active Findings & Fitness-for-Service Dossiers
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Verified multi-sensor detections, remaining wall calculations, and CMMS dispatch status.
            </p>
          </div>

          {/* Filter Tabs & Search Bar */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter Pills */}
            <div className="bg-white border border-slate-200 p-0.5 rounded-lg flex items-center text-xs font-semibold">
              <button
                onClick={() => setActiveStatusTab('ALL')}
                className={`px-3 py-1.5 rounded-md transition-colors ${
                  activeStatusTab === 'ALL' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All ({INITIAL_FINDINGS.length})
              </button>
              <button
                onClick={() => setActiveStatusTab('CRITICAL')}
                className={`px-3 py-1.5 rounded-md transition-colors ${
                  activeStatusTab === 'CRITICAL' ? 'bg-red-600 text-white shadow-xs' : 'text-slate-600 hover:text-red-600'
                }`}
              >
                Critical (1)
              </button>
              <button
                onClick={() => setActiveStatusTab('RERATE')}
                className={`px-3 py-1.5 rounded-md transition-colors ${
                  activeStatusTab === 'RERATE' ? 'bg-amber-600 text-white shadow-xs' : 'text-slate-600 hover:text-amber-600'
                }`}
              >
                Rerate (2)
              </button>
              <button
                onClick={() => setActiveStatusTab('PASS')}
                className={`px-3 py-1.5 rounded-md transition-colors ${
                  activeStatusTab === 'PASS' ? 'bg-green-600 text-white shadow-xs' : 'text-slate-600 hover:text-green-600'
                }`}
              >
                Pass (3)
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search tag, line, system..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
              />
            </div>
          </div>
        </div>

        {/* Table Body */}
        <div className="overflow-x-auto">
          <table className="table text-xs sm:text-sm">
            <thead>
              <tr className="bg-slate-100/75 border-b border-slate-200">
                <th className="py-3 px-4 font-mono font-bold text-slate-700">ASSET TAG / SYSTEM</th>
                <th className="py-3 px-4 font-bold text-slate-700">SCREENING METHOD</th>
                <th className="py-3 px-4 font-mono font-bold text-slate-700">WALL THICKNESS</th>
                <th className="py-3 px-4 font-mono font-bold text-slate-700">RSF / IPS</th>
                <th className="py-3 px-4 font-bold text-slate-700">DISPOSITION</th>
                <th className="py-3 px-4 font-mono font-bold text-slate-700">CMMS ORDER</th>
                <th className="py-3 px-4 font-bold text-right text-slate-700">ACTION</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-sans">
              {filteredFindings.map((f) => {
                const isExpanded = expandedFindingId === f.id;
                return (
                  <tr key={f.id} className="hover:bg-slate-50/90 transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-mono font-bold text-slate-900">{f.assetTag}</div>
                      <div className="text-xs text-slate-500 font-sans">{f.component} • {f.system}</div>
                      <div className="text-[11px] font-mono text-slate-400 mt-0.5">
                        Op Temp: <strong className="text-amber-700">{f.opTemp}°C</strong> (CUI Window)
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <span className="telemetry-pill bg-slate-100 text-slate-700 border border-slate-200">
                        {f.method}
                      </span>
                      <div className="text-[11px] text-slate-400 mt-1">{f.lastInspected}</div>
                    </td>

                    <td className="py-3 px-4 font-mono">
                      <div className="text-slate-900 font-semibold">
                        t_min: <span className={f.measuredWall < f.nominalWall * 0.7 ? 'text-red-600 font-bold' : 'text-slate-800'}>{f.measuredWall.toFixed(2)} mm</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        t_nom: {f.nominalWall.toFixed(1)} mm ({(((f.nominalWall - f.measuredWall) / f.nominalWall) * 100).toFixed(0)}% loss)
                      </div>
                    </td>

                    <td className="py-3 px-4 font-mono">
                      <div className="font-bold text-slate-900">
                        RSF: <span className={f.rsf < 0.9 ? 'text-amber-600 font-bold' : 'text-green-700'}>{f.rsf.toFixed(3)}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        IPS Risk: <strong className="text-slate-700">{f.ipsScore} / 10</strong>
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <span
                        className={`badge font-mono font-semibold ${
                          f.status === 'PASS_MAWP'
                            ? 'badge-success'
                            : f.status === 'RERATE_REQUIRED'
                            ? 'badge-warning'
                            : 'badge-danger'
                        }`}
                      >
                        {f.status === 'PASS_MAWP' ? (
                          <CheckCircle2 className="h-3 w-3 mr-1 inline" />
                        ) : f.status === 'RERATE_REQUIRED' ? (
                          <AlertTriangle className="h-3 w-3 mr-1 inline" />
                        ) : (
                          <XCircle className="h-3 w-3 mr-1 inline" />
                        )}
                        {f.status}
                      </span>
                    </td>

                    <td className="py-3 px-4 font-mono text-xs">
                      {f.workOrder !== 'N/A' ? (
                        <div>
                          <div className="font-semibold text-blue-700">{f.workOrder}</div>
                          <span className={`text-[10px] uppercase ${f.workOrderStatus === 'synced' ? 'text-green-600' : 'text-amber-600'}`}>
                            ● {f.workOrderStatus}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400">Not Required</span>
                      )}
                    </td>

                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => setExpandedFindingId(isExpanded ? null : f.id)}
                        className="btn btn-secondary text-xs py-1 px-2.5 inline-flex items-center gap-1 shadow-sm"
                      >
                        <span>{isExpanded ? 'Hide' : 'Audit'}</span>
                        {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Expandable Technical Detail Drawer for Selected Finding */}
        {expandedFindingId && (() => {
          const finding = INITIAL_FINDINGS.find((f) => f.id === expandedFindingId);
          if (!finding) return null;
          return (
            <div className="bg-slate-900 text-slate-200 p-5 border-t border-slate-700 font-mono text-xs space-y-4 animate-fade-in">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-3">
                <div className="flex items-center gap-2 text-white font-bold text-sm">
                  <ShieldCheck className="h-4 w-4 text-blue-400" />
                  <span>DETERMINISTIC COMPLIANCE RECORD // ASSET: {finding.assetTag} ({finding.id})</span>
                </div>
                <div className="text-slate-400 text-[11px]">
                  LOCATION: {finding.coordinates}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-slate-800/80 p-3 rounded border border-slate-700">
                  <div className="text-[10px] text-slate-400 uppercase">ASME / API 579-1 PARAMETERS</div>
                  <div className="text-white mt-1 space-y-1">
                    <div>Folias Bulging Factor (Mt): <strong>{finding.details.foliasMt}</strong></div>
                    <div>Flaw Length (2c): <strong>{finding.details.flawLength} mm</strong></div>
                    <div>Design MAWP: <strong>{finding.details.mawp} MPa</strong></div>
                    {finding.details.deratedMawp && (
                      <div className="text-amber-400 font-bold">
                        Derated MAWP: {finding.details.deratedMawp} MPa
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-slate-800/80 p-3 rounded border border-slate-700">
                  <div className="text-[10px] text-slate-400 uppercase">DEGRADATION AUDIT</div>
                  <div className="text-white mt-1 space-y-1">
                    <div>Future Corrosion Allowance: <strong>{finding.details.corrosionAllowance} mm</strong></div>
                    <div>Screening Modality: <strong>{finding.method}</strong></div>
                    <div>Inspection Confidence: <strong>96.8% Certified</strong></div>
                  </div>
                </div>

                <div className="bg-slate-800/80 p-3 rounded border border-slate-700 flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] text-slate-400 uppercase">RECOMMENDED INTERVENTION</div>
                    <p className="text-slate-300 font-sans text-xs mt-1 leading-relaxed">
                      {finding.details.recommendedAction}
                    </p>
                  </div>
                  <div className="pt-2 mt-2 border-t border-slate-700/60 flex items-center justify-between">
                    <Link to="/cmms" className="text-blue-400 hover:underline text-xs flex items-center gap-1">
                      <Wrench className="h-3 w-3" />
                      <span>Manage CMMS Dispatch</span>
                    </Link>
                    <Link to="/tools/ffs" className="text-blue-400 hover:underline text-xs">
                      Re-run FFS Model →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      </div>
    </div>
  );
}
